import { DayObject, DayRange } from "@/types/date"
import { TimeFrame } from "@/types/trip"
import isEmpty from "lodash/isEmpty"
import isEqual from "lodash/isEqual"

import {
  addDays,
  browserToday,
  daysInDayRange,
  rangeContainsDay,
  today,
} from "./date-utils"

export function isFixedTimeFrame(timeFrame: TimeFrame) {
  return timeFrame.start_date && timeFrame.duration
}

export function hasFixedStartDate(timeFrame: TimeFrame) {
  return !!timeFrame.start_date
}

// Does the time frame allow a trip at any time, any duration?
// Useful for making UI that differs when the user hasn't
// entered any time frame info at all, yet.
export function isWideOpenTimeFrame(timeFrame: TimeFrame) {
  return (
    !timeFrame.start_date && !timeFrame.duration && isEmpty(timeFrame.months)
  )
}

export function equalTimeFrames(timeFrameA: TimeFrame, timeFrameB: TimeFrame) {
  // Better than isEqual because it will consider a missing
  // value and an undefined value as the same.
  return (
    timeFrameA.start_date === timeFrameB.start_date &&
    timeFrameA.duration === timeFrameB.duration &&
    ((isEmpty(timeFrameA.months) && isEmpty(timeFrameB.months)) ||
      isEqual(timeFrameA.months, timeFrameB.months)) // assume sorted
  )
}

export function timeFrameForDayRange(
  dayRange: DayRange,
  fallbackDuration?: number
): TimeFrame {
  return {
    start_date: dayRange.from,
    duration:
      dayRange.from && dayRange.to
        ? daysInDayRange(dayRange)
        : dayRange.from
          ? fallbackDuration
          : undefined,
  }
}

export function dayRangeForTimeFrame(
  timeFrame: TimeFrame | undefined
): DayRange {
  return {
    from: timeFrame?.start_date,
    to:
      timeFrame?.start_date && timeFrame.duration
        ? addDays(timeFrame.start_date, timeFrame.duration - 1)
        : undefined,
  }
}

export function dayRangeForDraftDates(
  draftStartDate: DayObject,
  draftDays: number
) {
  return {
    from: draftStartDate,
    to: addDays(draftStartDate, draftDays - 1),
  }
}

export function timeFrameIsOutsideCurrentYear(timeFrame: TimeFrame): boolean {
  if (!timeFrame.start_date) {
    return false
  }

  const currentYear = browserToday().year
  if (timeFrame.start_date.year !== currentYear) {
    return true
  }

  if (timeFrame.duration) {
    const { to } = dayRangeForTimeFrame(timeFrame)
    return !!to && to.year !== currentYear
  }

  return false
}

export function isTimeFrameCurrentlyActive(
  timeFrame: TimeFrame,
  timeZone: string
): boolean {
  if (!isFixedTimeFrame(timeFrame)) {
    return false
  }

  const dayRange = dayRangeForTimeFrame(timeFrame)
  if (!dayRange.from || !dayRange.to) {
    return false
  }

  const todayInTimeZone = today(timeZone)
  return rangeContainsDay(
    { from: dayRange.from, to: dayRange.to },
    todayInTimeZone
  )
}
